/**
 * Audio utilities for the Interview component
 * Handles audio playback, processing, and related functionality
 */

/**
 * Play audio from base64 data with enhanced error handling and callbacks
 * @param {string} base64Audio - Base64 encoded audio data
 * @param {string} format - Audio format (default: "audio/wav")
 * @param {string} text - Associated text for captions
 * @param {function} onEndedCallback - Callback when audio ends
 * @param {Object} callbacks - Object containing state setters and other callbacks
 */
export const playAudioFromBase64 = (
  base64Audio,
  format = "audio/wav",
  text = "",
  onEndedCallback = null,
  callbacks = {}
) => {
  const {
    setShowLoader,
    setIsSpeaking,
    setBotSpeechCaption,
    setConversationTranscript,
    setLiveCaption, // Add this to handle live caption clearing
    onAudioStart,
    onAudioEnd,
    onAudioError
  } = callbacks;

  // Validate input
  if (!base64Audio) {
    console.error('❌ No audio data provided');
    return null;
  }

  try {
    console.log('🎵 Starting audio playback from base64');
    
    // Decode base64 to binary
    const binaryString = atob(base64Audio);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }

    // Create blob and audio element
    const blob = new Blob([bytes], { type: format });
    const audioUrl = URL.createObjectURL(blob);
    const audio = new Audio(audioUrl);
    
    audio.volume = 0.8;
    audio.preload = 'auto';

    // Set up event handlers
    audio.onloadstart = () => {
      console.log('🎵 Audio loading started');
      setShowLoader?.(false);
      setIsSpeaking?.(true);
      setLiveCaption?.(''); // Clear live caption when bot starts speaking
      
      // Show bot speech caption when audio starts
      if (text) {
        setBotSpeechCaption?.(text);
        
        // Add to conversation transcript
        setConversationTranscript?.(prev => [
          ...prev,
          {
            type: "bot",
            text: text,
            timestamp: new Date().toLocaleTimeString(),
          },
        ]);
      }
      
      onAudioStart?.(text);
    };

    audio.onplay = () => {
      console.log('🎵 Audio playback started');
      setIsSpeaking?.(true);
      setLiveCaption?.(''); // Clear live caption during bot speech
    };

    audio.onended = () => {
      console.log('🎵 Audio playback ended');
      setIsSpeaking?.(false);
      setBotSpeechCaption?.("");
      URL.revokeObjectURL(audioUrl);
      
      // Call custom callback if provided
      if (onEndedCallback && typeof onEndedCallback === "function") {
        try {
          onEndedCallback();
        } catch (error) {
          console.error("🎵 Error executing audio callback:", error);
        }
      }
      
      onAudioEnd?.();
    };

    audio.onerror = (error) => {
      // Silently handle audio errors during normal operation
      // These often occur during normal audio transitions and don't indicate real problems
      setIsSpeaking?.(false);
      setBotSpeechCaption?.("");
      URL.revokeObjectURL(audioUrl);
      onAudioError?.(error);
    };

    audio.onabort = () => {
      // Silently handle audio abort - this is normal during audio transitions
      setIsSpeaking?.(false);
      setBotSpeechCaption?.("");
      URL.revokeObjectURL(audioUrl);
    };

    // Start playback
    const playPromise = audio.play();
    
    if (playPromise !== undefined) {
      playPromise.catch(error => {
        console.error('❌ Failed to start audio playback:', error);
        setIsSpeaking?.(false);
        setBotSpeechCaption?.("");
        URL.revokeObjectURL(audioUrl);
        onAudioError?.(error);
      });
    }

    return audio; // Return audio element for external control if needed

  } catch (error) {
    console.error('❌ Error processing base64 audio:', error);
    setIsSpeaking?.(false);
    setBotSpeechCaption?.("");
    onAudioError?.(error);
    return null;
  }
};

/**
 * Stop any currently playing audio
 * @param {HTMLAudioElement} audioElement - Audio element to stop
 * @param {Object} callbacks - State setters
 */
export const stopAudio = (audioElement, callbacks = {}) => {
  const { setIsSpeaking, setBotSpeechCaption, setShowLoader, setLiveCaption } = callbacks;

  if (audioElement) {
    try {
      audioElement.pause();
      audioElement.currentTime = 0;
      audioElement.src = '';
      audioElement.load(); // Reset the audio element
    } catch (error) {
      console.warn('⚠️ Error stopping audio:', error);
    }
  }

  // Reset UI state
  setIsSpeaking?.(false);
  setBotSpeechCaption?.("");
  setShowLoader?.(false);
  setLiveCaption?.("");
};

/**
 * Create a WAV blob from raw audio data
 * @param {Float32Array} audioData - Raw audio data
 * @param {number} sampleRate - Sample rate (default: 16000)
 * @param {number} numChannels - Number of channels (default: 1)
 * @returns {Blob} WAV audio blob
 */
export const createWAVBlob = (audioData, sampleRate = 16000, numChannels = 1) => {
  const buffer = new ArrayBuffer(44 + audioData.length * 2);
  const view = new DataView(buffer);

  // Helper function to write string to buffer
  const writeString = (offset, string) => {
    for (let i = 0; i < string.length; i++) {
      view.setUint8(offset + i, string.charCodeAt(i));
    }
  };

  // WAV header
  writeString(0, 'RIFF');
  view.setUint32(4, 36 + audioData.length * 2, true);
  writeString(8, 'WAVE');
  writeString(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * numChannels * 2, true);
  view.setUint16(32, numChannels * 2, true);
  view.setUint16(34, 16, true);
  writeString(36, 'data');
  view.setUint32(40, audioData.length * 2, true);

  // Convert float audio data to 16-bit PCM
  for (let i = 0; i < audioData.length; i++) {
    const sample = Math.max(-1, Math.min(1, audioData[i]));
    view.setInt16(44 + i * 2, sample * 0x7FFF, true);
  }

  return new Blob([buffer], { type: 'audio/wav' });
};

/**
 * Convert ArrayBuffer to base64 string
 * @param {ArrayBuffer} buffer - ArrayBuffer to convert
 * @returns {string} Base64 string
 */
export const arrayBufferToBase64 = (buffer) => {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  bytes.forEach(byte => binary += String.fromCharCode(byte));
  return btoa(binary);
};

/**
 * Detect speech activity in audio data
 * @param {Float32Array} audioData - Audio data to analyze
 * @param {number} threshold - Speech detection threshold (default: 0.003)
 * @returns {Object} Speech analysis results
 */
export const detectSpeechActivity = (audioData, threshold = 0.003) => {
  if (!audioData || audioData.length === 0) {
    return {
      isSpeaking: false,
      averageLevel: 0,
      peak: 0,
      energy: 0,
      activityRatio: 0
    };
  }

  let sum = 0;
  let peak = 0;
  let energySum = 0;
  let aboveThresholdCount = 0;

  for (let i = 0; i < audioData.length; i++) {
    const abs = Math.abs(audioData[i]);
    sum += abs;
    peak = Math.max(peak, abs);
    energySum += abs * abs;
    
    if (abs > (threshold * 0.5)) {
      aboveThresholdCount++;
    }
  }

  const averageLevel = sum / audioData.length;
  const energy = Math.sqrt(energySum / audioData.length);
  const activityRatio = aboveThresholdCount / audioData.length;

  // Multiple detection methods for improved accuracy
  const isSpeaking = (
    averageLevel > threshold ||
    peak > (threshold * 4) ||
    energy > (threshold * 2) ||
    activityRatio > 0.1
  );

  return {
    isSpeaking,
    averageLevel,
    peak,
    energy,
    activityRatio
  };
};

/**
 * Format time in MM:SS format
 * @param {number} seconds - Time in seconds
 * @returns {string} Formatted time string
 */
export const formatTime = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

/**
 * Check if browser supports speech recognition
 * @returns {boolean} True if supported
 */
export const isSpeechRecognitionSupported = () => {
  return !!(window.SpeechRecognition || window.webkitSpeechRecognition);
};

/**
 * Check if browser supports AudioWorklet
 * @returns {boolean} True if supported
 */
export const isAudioWorkletSupported = () => {
  return !!(window.AudioWorklet && window.AudioContext);
};

/**
 * Get optimal audio constraints for the current browser
 * @returns {Object} Audio constraints object
 */
export const getOptimalAudioConstraints = () => {
  const isChrome = /Chrome/.test(navigator.userAgent) && /Google Inc/.test(navigator.vendor);
  const isSafari = /Safari/.test(navigator.userAgent) && /Apple Computer/.test(navigator.vendor);

  const baseConstraints = {
    echoCancellation: true,
    noiseSuppression: true,
    autoGainControl: true,
    channelCount: 1
  };

  if (isChrome) {
    return {
      ...baseConstraints,
      sampleRate: 16000,
      googEchoCancellation: true,
      googAutoGainControl: true,
      googNoiseSuppression: true,
      googHighpassFilter: true,
      googTypingNoiseDetection: true
    };
  } else if (isSafari) {
    return {
      ...baseConstraints,
      sampleRate: 16000
    };
  } else {
    return {
      ...baseConstraints,
      sampleRate: 16000
    };
  }
};

export default {
  playAudioFromBase64,
  stopAudio,
  createWAVBlob,
  arrayBufferToBase64,
  detectSpeechActivity,
  formatTime,
  isSpeechRecognitionSupported,
  isAudioWorkletSupported,
  getOptimalAudioConstraints
};
